// Shared model for the Document knowledge Enrichment: palette, stages, run hook.
import { useEffect, useRef, useState } from "react";
import {
  ScanText,
  ListTree,
  LayoutGrid,
  CheckCircle2,
  Share2,
  Network,
  Brain,
  Hexagon,
  FileCheck,
  CircleDot,
  Link2,
  AlignLeft,
  Star,
  Landmark,
  Split,
  GitCompare,
  type LucideIcon,
} from "lucide-react";
import { JOB_PHASE, type JobPhase } from "./constants";

export { YIELD_TONE_CLASS, type ConsoleTone } from "./constants";
export {
  buildResultTone,
  buildYield,
  buildStageStates,
  buildStageStatesFromEvents,
  type ConsoleYield,
  type ConsoleStageState,
} from "./utils";

export const ACCENT_VARS = {
  primary: "var(--primary)",
  chart1: "var(--chart-1)",
  chart2: "var(--chart-2)",
  chart3: "var(--chart-3)",
  chart4: "var(--chart-4)",
  chart5: "var(--chart-5)",
} as const;

export interface ConsolePalette {
  background: string;
  card: string;
  border: string;
  foreground: string;
  mutedForeground: string;
  primary: string;
  chart: [string, string, string, string, string];
}

const PALETTE_FALLBACK: ConsolePalette = {
  background: "#f5f7fb",
  card: "#ffffff",
  border: "#e2e8f0",
  foreground: "#0f172a",
  mutedForeground: "#475569",
  primary: "#4f46e5",
  chart: ["#4f46e5", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"],
};

export function readPalette(): ConsolePalette {
  if (typeof window === "undefined") return PALETTE_FALLBACK;
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) =>
    s.getPropertyValue(name).trim() || fallback;
  return {
    background: v("--background", PALETTE_FALLBACK.background),
    card: v("--card", PALETTE_FALLBACK.card),
    border: v("--border", PALETTE_FALLBACK.border),
    foreground: v("--foreground", PALETTE_FALLBACK.foreground),
    mutedForeground: v("--muted-foreground", PALETTE_FALLBACK.mutedForeground),
    primary: v("--primary", PALETTE_FALLBACK.primary),
    chart: [
      v("--chart-1", PALETTE_FALLBACK.chart[0]),
      v("--chart-2", PALETTE_FALLBACK.chart[1]),
      v("--chart-3", PALETTE_FALLBACK.chart[2]),
      v("--chart-4", PALETTE_FALLBACK.chart[3]),
      v("--chart-5", PALETTE_FALLBACK.chart[4]),
    ],
  };
}

export function isDarkTheme(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

export interface PipelineStage {
  key: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  accent: string;
  accentIndex: number;
  durationMs: number | null;
}

/*
 * The 11-stage pipeline the backend actually runs, in execution order.
 * Ordering matters — the top strip and the Validation Feed both key off this array.
 * `durationMs = null` marks the hold stage where the animation waits for the
 * backend to signal completion; only "validate" is null so the LLM validation
 * layers can take as long as they need before the remaining stages animate.
 */
export const PIPELINE_STAGES: PipelineStage[] = [
  {
    key: "ingestion",
    label: "Ingestion",
    hint: "Bytes accepted and the document record written to storage",
    icon: ScanText,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
    durationMs: 1400,
  },
  {
    key: "structure",
    label: "Structure",
    hint: "Headings, sections and the page map extracted from the file",
    icon: ListTree,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
    durationMs: 900,
  },
  {
    key: "canonical_record",
    label: "Canonical record",
    hint: "Obligations, definitions and entities pulled into a canonical form",
    icon: FileCheck,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
    durationMs: 1200,
  },
  {
    key: "semantic_chunking",
    label: "Semantic chunking",
    hint: "The extractor cuts the text on meaning, not fixed length",
    icon: Brain,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
    durationMs: 1000,
  },
  {
    key: "chunk_selection",
    label: "Chunk selection",
    hint: "Semantic chunks are picked; falls back to 800-char passages if needed",
    icon: LayoutGrid,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
    durationMs: 700,
  },
  {
    key: "embed",
    label: "Embed",
    hint: "Each passage turned into a vector for search and similarity",
    icon: Share2,
    accent: ACCENT_VARS.chart3,
    accentIndex: 2,
    durationMs: 700,
  },
  {
    key: "hub",
    label: "Hub",
    hint: "Committed and searchable in the knowledge hub — first milestone",
    icon: Network,
    accent: ACCENT_VARS.chart3,
    accentIndex: 2,
    durationMs: 600,
  },
  {
    key: "validate",
    label: "Validate",
    hint: "The seven validation layers run against the text with a model",
    icon: CheckCircle2,
    accent: ACCENT_VARS.chart4,
    accentIndex: 3,
    durationMs: null,
  },
  {
    key: "classify",
    label: "Classify",
    hint: "Requirements mapped to SCF domains they belong to",
    icon: Hexagon,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
    durationMs: 900,
  },
  {
    key: "decompose",
    label: "Decompose",
    hint: "Each requirement split into actor, action and object",
    icon: Split,
    accent: ACCENT_VARS.chart5,
    accentIndex: 4,
    durationMs: 1000,
  },
  {
    key: "compare",
    label: "Compare",
    hint: "Requirements checked against selected compare-with documents — analysis complete",
    icon: GitCompare,
    accent: ACCENT_VARS.chart4,
    accentIndex: 3,
    durationMs: 1100,
  },
];

export const HOLD_STAGE_INDEX = PIPELINE_STAGES.findIndex(
  (s) => s.durationMs === null,
);

export const ANIMATION_HOLD_INDEX = PIPELINE_STAGES.findIndex(
  (s) => s.key === "structure",
);

export interface ValidationLayer {
  key: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  accent: string;
  accentIndex: number;
}

export const VALIDATION_LAYERS: ValidationLayer[] = [
  {
    key: "domain",
    label: "Domain",
    hint: "Subject areas the document governs",
    icon: Hexagon,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
  },
  {
    key: "concept",
    label: "Concept",
    hint: "Terms and definitions introduced",
    icon: CircleDot,
    accent: ACCENT_VARS.chart3,
    accentIndex: 2,
  },
  {
    key: "requirement",
    label: "Requirement",
    hint: "Obligations stated in the text",
    icon: FileCheck,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
  },
  {
    key: "relationship",
    label: "Relationship",
    hint: "How requirements and risks connect",
    icon: Link2,
    accent: ACCENT_VARS.chart4,
    accentIndex: 3,
  },
  {
    key: "consistency",
    label: "Consistency",
    hint: "Internal contradictions and gaps",
    icon: AlignLeft,
    accent: ACCENT_VARS.chart5,
    accentIndex: 4,
  },
  {
    key: "quality",
    label: "Knowledge Quality",
    hint: "Clarity and completeness of what was extracted",
    icon: Star,
    accent: ACCENT_VARS.chart1,
    accentIndex: 0,
  },
  {
    key: "governance",
    label: "Governance",
    hint: "Ownership, approval and review controls",
    icon: Landmark,
    accent: ACCENT_VARS.chart2,
    accentIndex: 1,
  },
];

export type ConsolePhase = JobPhase;

export interface PipelineRun {
  stage: number;
  done: boolean;
  elapsed: number;
  progress: number;
  layersLit: number;
  apiDurationMs: number | null;
}

export function formatDuration(ms: number): string {
  const total = ms / 1000;
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
}

export function usePipelineRun(
  phase: ConsolePhase,
  onFinished?: () => void,
  apiDurationMs: number | null = null,
  apiClearedLayers: number | null = null,
): PipelineRun {
  const [stage, setStage] = useState(0);
  const [done, setDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [layersLit, setLayersLit] = useState(0);
  const finishedRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    const start = Date.now();
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      250,
    );
    return () => clearInterval(t);
  }, []);

  const paceFactor =
    apiDurationMs === null
      ? 1
      : Math.min(1.6, Math.max(0.35, apiDurationMs / 6000));

  useEffect(() => {
    const uploadingHold =
      stage >= ANIMATION_HOLD_INDEX && phase === JOB_PHASE.UPLOADING;
    /*
     * Only the null-duration stage (`validate`) waits for the backend.
     * `stage >= HOLD_STAGE_INDEX` would also freeze the post-validate stages
     * (classify / decompose / compare) whenever the backend keeps
     * `phase === RUNNING` — those stages have real durationMs values and
     * advance on their own timers once the animation clears validate.
     */
    const validatingHold =
      stage === HOLD_STAGE_INDEX && phase === JOB_PHASE.RUNNING;
    if (uploadingHold || validatingHold) return;

    const wait = (PIPELINE_STAGES[stage].durationMs ?? 600) * paceFactor;

    if (stage >= PIPELINE_STAGES.length - 1) {
      const t = setTimeout(() => {
        setDone(true);
        if (!finishedRef.current) {
          finishedRef.current = true;
          onFinishedRef.current?.();
        }
      }, wait);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => setStage((s) => s + 1), wait);
    return () => clearTimeout(t);
  }, [stage, phase, done, paceFactor]);

  useEffect(() => {
    if (phase === JOB_PHASE.ERROR) setDone(true);
  }, [phase]);

  useEffect(() => {
    setLayersLit(
      apiClearedLayers === null
        ? 0
        : Math.max(0, Math.min(apiClearedLayers, VALIDATION_LAYERS.length)),
    );
  }, [apiClearedLayers]);

  const progress = done
    ? 100
    : Math.round((stage / PIPELINE_STAGES.length) * 100);

  return { stage, done, elapsed, progress, layersLit, apiDurationMs };
}

export type { ValidateFileResult } from "./api/contentRegistryApi";
